import { useState, useEffect, useCallback } from 'react';
import { teamService } from '../../../services/teamService';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { CompanyMember, PendingInvitation } from '../../../types/team';

interface UseTeamDataParams {
  companyId: number | null;
}

export function useTeamData({ companyId }: UseTeamDataParams) {
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);

  // Invite dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccessMessage, setInviteSuccessMessage] = useState<string | null>(null);

  const currentMember = members.find((m) => m.userId === currentUserId);
  const isOwner = currentMember?.role === 'OWNER';

  const fetchMembers = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const [data, me] = await Promise.all([
        teamService.getMembers(companyId),
        teamService.getCurrentUser(),
      ]);
      setMembers(data);
      setCurrentUserId(me.userId);
    } catch {
      setError('Не вдалося завантажити учасників команди');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // Only owners can see pending invitations (backend enforces this too) — wait for members to
  // load first so we know the current user's role before asking.
  useEffect(() => {
    if (!companyId || !isOwner) {
      setPendingInvitations([]);
      return;
    }
    teamService.getPendingInvitations(companyId)
      .then(setPendingInvitations)
      .catch(() => setPendingInvitations([]));
  }, [companyId, isOwner]);

  const inviteMember = async (email: string): Promise<string | null> => {
    if (!companyId) return 'Ідентифікатор компанії відсутній';

    setInviteLoading(true);
    setInviteError(null);
    setInviteSuccessMessage(null);
    try {
      await teamService.inviteByEmail(companyId, email);

      setInviteSuccessMessage(`Запрошення надіслано на ${email}`);
      teamService.getPendingInvitations(companyId)
        .then(setPendingInvitations)
        .catch(() => {});

      setInviteOpen(false);
      return null;
    } catch (err) {
      const message = extractErrorMessage(err, 'Не вдалося запросити учасника');
      setInviteError(message);
      return message;
    } finally {
      setInviteLoading(false);
    }
  };

  const changeRole = async (userId: number, role: 'OWNER' | 'MANAGER') => {
    if (!companyId) return;
    try {
      const updated = await teamService.changeRole(companyId, userId, { role });
      setMembers((prev) => prev.map((m) => (m.userId === userId ? updated : m)));
    } catch {
      setError('Не вдалося змінити роль');
    }
  };

  const removeMember = async (userId: number) => {
    if (!companyId) return;
    try {
      await teamService.removeMember(companyId, userId);
      setMembers((prev) => prev.filter((m) => m.userId !== userId));
    } catch {
      setError('Не вдалося видалити учасника');
    }
  };

  const cancelInvitation = async (invitationId: number) => {
    if (!companyId) return;
    try {
      await teamService.cancelInvitation(companyId, invitationId);
      setPendingInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));
    } catch {
      setError('Не вдалося скасувати запрошення');
    }
  };

  return {
    members,
    currentUserId,
    loading,
    error,
    pendingInvitations,
    invite: {
      open: inviteOpen,
      loading: inviteLoading,
      error: inviteError,
      successMessage: inviteSuccessMessage,
      setOpen: setInviteOpen,
      submit: inviteMember,
    },
    changeRole,
    removeMember,
    cancelInvitation,
  };
}
